/**
 * Execution Signal Types
 * 
 * These types define the contract for the Execution Signal system
 * as specified in docs/backend/EXO_PANEL_BACKEND_REQUIREMENTS.md
 * 
 * Use `getExecutionSignalService()` — main API by default (`/api/v1/execution-signals`); set `REACT_APP_EXO_EXECUTION_SIGNALS_MOCK=true` for offline mock.
 * Contract + golden JSON: `Krysalis_Backend/docs/frontend/EXECUTION_SIGNALS_API_CONTRACT.md`, `Krysalis_Backend/tests/fixtures/execution_signals_golden.json`.
 */

// Signal type enumeration
export type SignalType = 
  | 'transition_required'
  | 'action_required'
  | 'approval_needed'
  | 'capacity_exceeded'
  | 'exception_raised'
  | 'input_required';

// Signal status in lifecycle
export type SignalStatus = 
  | 'pending'
  | 'delivered'
  | 'acknowledged'
  | 'resolved'
  | 'escalated'
  | 'cancelled';

// Resolution types (user actions)
export type ResolutionType = 
  | 'completed'
  | 'blocked'
  | 'clarification_requested'
  | 'escalated';

// Block reasons (exception paths per UX Principle #7)
export type BlockReason =
  | 'external_dependency'
  | 'missing_resource'
  | 'conflicting_signal'
  | 'invalid_precondition'
  | 'insufficient_authority'
  | 'other';

// Block reason display labels
export const BLOCK_REASON_LABELS: Record<BlockReason, string> = {
  external_dependency: 'External Dependency',
  missing_resource: 'Missing Resource',
  conflicting_signal: 'Conflicting Signal',
  invalid_precondition: 'Invalid Precondition',
  insufficient_authority: 'Insufficient Authority',
  other: 'Other'
};

// Context information attached to a signal
export interface SignalContext {
  artifact_type?: string;
  artifact_name?: string;
  artifact_id?: string;
  item_id?: string;
  current_state?: string;
  target_state?: string;
  blocking_reason?: string;
  waiting_on?: string[];
  [key: string]: unknown;
}

// Main execution signal interface
export interface ExecutionSignal {
  id: string;
  signal_type: SignalType;
  priority: number; // 1 (highest) to 10 (lowest)
  message: string; // Human-readable message from KRYBOT
  context: SignalContext;
  status: SignalStatus;
  available_actions: ResolutionType[];
  created_at: string;
  delivered_at?: string;
  acknowledged_at?: string;
  resolved_at?: string;
  resolution_type?: ResolutionType;
  resolution_reason?: BlockReason;
  resolution_notes?: string;
}

// Queue status for visibility
export interface QueueStatus {
  total_pending: number;
  high_priority: number;
  blocked_count: number;
}

// Exo Panel overall status (for "silence is valid" states)
export type ExoPanelStatusType = 
  | 'no_action_required'
  | 'signals_pending'
  | 'waiting_upstream'
  | 'system_stable';

export interface ExoPanelStatus {
  status: ExoPanelStatusType;
  message: string;
  next_expected_at?: string;
  waiting_on: string[];
}

// Response from getNextSignal API
export interface NextSignalResponse {
  signal: ExecutionSignal | null;
  queue_status: QueueStatus;
}

// Request bodies for resolution actions
export interface ResolveSignalRequest {
  resolution_type: ResolutionType;
  notes?: string;
}

export interface BlockSignalRequest {
  reason: BlockReason;
  notes?: string;
  expected_resolution_date?: string;
}

export interface EscalateSignalRequest {
  escalation_reason: string;
  escalate_to_role?: string;
}

export interface ClarificationRequest {
  question: string;
}

// API Service interface (for future backend integration)
export interface IExecutionSignalService {
  getNextSignal(): Promise<NextSignalResponse>;
  getPendingSignals(params?: { 
    status?: string; 
    priority_max?: number; 
    limit?: number 
  }): Promise<ExecutionSignal[]>;
  getSignal(signalId: string): Promise<ExecutionSignal>;
  resolveSignal(signalId: string, resolution: ResolveSignalRequest): Promise<void>;
  blockSignal(signalId: string, block: BlockSignalRequest): Promise<void>;
  escalateSignal(signalId: string, escalation: EscalateSignalRequest): Promise<void>;
  requestClarification(signalId: string, clarification: ClarificationRequest): Promise<void>;
  getStatus(): Promise<ExoPanelStatus>;
}
